import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  message,
  Select,
  Divider,
  Tooltip,
} from "antd";
import {
  CarOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { updateVehicle } from "../../api/vehicle/vehicleRequest";
import { colorMap, commonBrands, commonColors } from "../../utils/themeConstant";
import { countries } from "../../utils/countries";

const { Option } = Select;

const VehicleEditModal = ({
  visible,
  vehicleData,
  onCancel,
  onUpdateVehicle,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [brands, setBrands] = useState([]);
  const [colors, setColors] = useState([]);
  const [newBrand, setNewBrand] = useState("");
  const [newColor, setNewColor] = useState("");
  const [colorHex, setColorHex] = useState("#CCCCCC");
  const [showAddBrand, setShowAddBrand] = useState(false);
  const [showAddColor, setShowAddColor] = useState(false);

  useEffect(() => {
    // Initialize brands and colors lists
    setBrands([...commonBrands]);
    setColors([...commonColors]);
  }, []);

  useEffect(() => {
    if (visible && vehicleData) {
      // Initialiser le formulaire avec les données du véhicule
      form.setFieldsValue({
        matricule: vehicleData.matricule,
        country: vehicleData.country || "MA",
        brand: vehicleData.brand,
        model: vehicleData.model,
        color: vehicleData.color,
      });
    }
  }, [visible, vehicleData, form]);

  const handleAddBrand = () => {
    if (newBrand && newBrand.trim() !== "") {
      // Check if brand already exists
      if (brands.includes(newBrand.trim())) {
        message.warning("Cette marque existe déjà !");
        return;
      }

      const updatedBrands = [...brands, newBrand.trim()];
      setBrands(updatedBrands);
      form.setFieldsValue({ brand: newBrand.trim() });
      setNewBrand("");
      setShowAddBrand(false);
      message.success("Marque ajoutée avec succès");
    } else {
      message.error("Veuillez saisir un nom de marque valide");
    }
  };

  const handleAddColor = () => {
    if (newColor && newColor.trim() !== "") {
      // Check if color already exists
      if (colors.includes(newColor.trim())) {
        message.warning("Cette couleur existe déjà !");
        return;
      }

      // Update the colorMap with the new color
      const updatedColorMap = { ...colorMap };
      updatedColorMap[newColor.trim()] = colorHex;

      // Add the new color to the list
      const updatedColors = [...colors, newColor.trim()];
      setColors(updatedColors);

      // Update the colorMap
      Object.assign(colorMap, updatedColorMap);

      // Set the form value
      form.setFieldsValue({ color: newColor.trim() });

      // Reset inputs
      setNewColor("");
      setColorHex("#CCCCCC");
      setShowAddColor(false);
      message.success("Couleur ajoutée avec succès");
    } else {
      message.error("Veuillez saisir un nom de couleur valide");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload = {
        brand: values.brand,
        model: values.model,
        color: values.color,
        country: values.country,
      };

      try {
        // Appel à l'API pour mettre à jour le véhicule
        await updateVehicle(vehicleData.id, payload);
        message.success("Véhicule mis à jour avec succès");

        // Mettre à jour les données du véhicule localement
        onUpdateVehicle({ ...vehicleData, ...payload });
        onCancel();
      } catch (error) {
        console.error("Erreur lors de la mise à jour du véhicule:", error);
        message.error(error.response?.data?.error || "Erreur lors de la mise à jour du véhicule");
      } finally {
        setSubmitting(false);
      }
    } catch (errorInfo) {
      console.log("Validation échouée:", errorInfo);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Brand dropdown render
  const brandDropdownRender = (menu) => (
    <>
      {menu}
      <Divider className="my-2" />
      {showAddBrand ? (
        <div className="px-3 pb-2">
          <Input
            placeholder="Saisir le nom de la marque"
            value={newBrand}
            onChange={(e) => setNewBrand(e.target.value)}
            className="mb-2"
          />
          <div className="flex justify-between">
            <Button size="small" onClick={() => setShowAddBrand(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleAddBrand}
              className="bg-indigo-600 hover:bg-indigo-700 border-0"
            >
              Ajouter
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-3 pb-2">
          <Button
            type="link"
            block
            icon={<PlusOutlined />}
            onClick={() => setShowAddBrand(true)}
            className="text-center text-indigo-600"
          >
            Ajouter une marque
          </Button>
        </div>
      )}
    </>
  );

  // Color dropdown render
  const colorDropdownRender = (menu) => (
    <>
      {menu}
      <Divider className="my-2" />
      {showAddColor ? (
        <div className="px-3 pb-2">
          <div className="flex mb-2">
            <Input
              placeholder="Nom de la couleur"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              className="mr-2"
            />
            <Tooltip title="Choisir une couleur">
              <Input
                type="color"
                value={colorHex}
                onChange={(e) => setColorHex(e.target.value)}
                style={{ width: "50px", cursor: "pointer" }}
              />
            </Tooltip>
          </div>
          <div className="flex justify-between">
            <Button size="small" onClick={() => setShowAddColor(false)}>
              Annuler
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleAddColor}
              className="bg-indigo-600 hover:bg-indigo-700 border-0"
            >
              Ajouter
            </Button>
          </div>
        </div>
      ) : (
        <div className="px-3 pb-2">
          <Button
            type="link"
            block
            icon={<PlusOutlined />}
            onClick={() => setShowAddColor(true)}
            className="text-center text-indigo-600"
          >
            Ajouter une couleur
          </Button>
        </div>
      )}
    </>
  );

  return (
    <Modal
      visible={visible}
      title={
        <div className="flex items-center">
          <CarOutlined className="text-indigo-600 mr-2 text-xl" />
          <span>Modifier le Véhicule</span>
        </div>
      }
      onCancel={onCancel}
      footer={[
        <Button key="back" icon={<CloseOutlined />} onClick={onCancel}>
          Annuler
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SaveOutlined />}
          loading={submitting}
          onClick={handleSubmit}
          className="bg-indigo-600 hover:bg-indigo-700 border-0"
        >
          Enregistrer
        </Button>,
      ]}
      width={600}
      destroyOnClose={true}
      className="vehicle-edit-modal"
    >
      <Form form={form} layout="vertical" className="pt-4">
        <div className="mb-2">Matricule</div>
        <Input
          value={vehicleData?.matricule}
          className="rounded-md mb-3"
          autoComplete="off"
          disabled={true}
        />

        <Form.Item
          name="country"
          label="Pays d'immatriculation"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner le pays d'immatriculation",
            },
          ]}
        >
          <Select
            showSearch
            placeholder="Sélectionnez un pays"
            optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            className="rounded-md"
          >
            {countries.map((country) => (
              <Option key={country.code} value={country.code}>
                {country.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="brand" label="Marque">
          <Select
            showSearch
            placeholder="Sélectionnez une marque"
            optionFilterProp="children"
            className="rounded-md"
            allowClear
            dropdownRender={brandDropdownRender}
          >
            {brands.map((brand) => (
              <Option key={brand} value={brand}>
                {brand}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="model" label="Modèle">
          <Input
            placeholder="Saisir le modèle"
            className="rounded-md"
            autoComplete="off"
          />
        </Form.Item>

        <Form.Item name="color" label="Couleur">
          <Select
            showSearch
            placeholder="Sélectionnez une couleur"
            optionFilterProp="children"
            className="rounded-md"
            allowClear
            dropdownRender={colorDropdownRender}
          >
            {colors.map((color) => (
              <Option key={color} value={color}>
                <div className="flex items-center">
                  <div
                    className="w-4 h-4 rounded-full mr-2"
                    style={{ backgroundColor: colorMap[color] || "#CCCCCC" }}
                  />
                  {color}
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {vehicleData && vehicleData.last_user_update && (
          <div className="text-gray-500 mt-4 text-sm">
            <p>
              Dernière modification par:{" "}
              {vehicleData.last_user_update.full_name || "Inconnu"}
            </p>
            <p>Date: {formatDate(vehicleData.updated_at)}</p>
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default VehicleEditModal;
